/**
 * Ethics Dashboard Service
 * 
 * Provides comprehensive ethics dashboard overview aggregating data from:
 * - Supply Chain Ethics (ESG compliance and monitoring)  
 * - Cultural & Global Ethics (regional compliance and cultural sensitivity)
 * - Supplier Ecosystem Ethics (supplier lifecycle management)
 * - Ethics Program Management (training, policies, incident management)
 * - Risk Management (ethics-related risk assessment and mitigation)
 * - Industry Standard Frameworks (SOX, ISO 37001, COSO, GRC)
 */

import { add as addAuditLog } from '../bread/auditLogService';

const ETHICS_DASHBOARD_KEY = 'ethics-dashboard-v1';

// Industry Standard Ethics Frameworks
const ETHICS_FRAMEWORKS = {
  sox: {
    name: 'Sarbanes-Oxley Act (SOX)',
    description: 'Financial reporting and corporate governance standards',
    sections: ['Section 302 (CEO/CFO Certification)', 'Section 404 (Internal Controls)', 'Section 906 (Criminal Penalties)'],
    complianceScore: 94.2,
    lastAssessment: '2024-08-15',
    status: 'Compliant'
  },
  iso37001: {
    name: 'ISO 37001 Anti-Bribery Management',
    description: 'International standard for anti-bribery management systems',
    sections: ['Risk Assessment', 'Due Diligence', 'Training', 'Monitoring'],
    complianceScore: 89.7,
    lastAssessment: '2024-09-01',
    status: 'Compliant'
  },
  coso: {
    name: 'COSO Internal Control Framework',
    description: 'Framework for designing, implementing and conducting internal controls',
    sections: ['Control Environment', 'Risk Assessment', 'Control Activities', 'Information & Communication', 'Monitoring'],
    complianceScore: 91.5,
    lastAssessment: '2024-08-20',
    status: 'Compliant'
  },
  ungc: {
    name: 'UN Global Compact',
    description: 'United Nations initiative on corporate sustainability and responsibility',
    sections: ['Human Rights', 'Labour', 'Environment', 'Anti-Corruption'],
    complianceScore: 87.3,
    lastAssessment: '2024-09-10',
    status: 'Action Required'
  },
  oecd: {
    name: 'OECD Guidelines for Multinational Enterprises',
    description: 'International standards for responsible business conduct',
    sections: ['General Policies', 'Disclosure', 'Human Rights', 'Employment', 'Environment', 'Competition'],
    complianceScore: 88.9,
    lastAssessment: '2024-08-25',
    status: 'Compliant'
  }
};

// Ethics Risk Categories
const ETHICS_RISK_CATEGORIES = {
  corruption: {
    name: 'Corruption & Bribery',
    level: 'Medium',
    score: 7.2,
    trend: 'Improving',
    controls: 12,
    incidents: 0,
    mitigation: ['Enhanced due diligence', 'Third-party monitoring', 'Anonymous reporting']
  },
  discrimination: {
    name: 'Discrimination & Harassment',
    level: 'Low',
    score: 3.1,
    trend: 'Stable',
    controls: 18,
    incidents: 2,
    mitigation: ['Bias training', 'Clear reporting channels', 'Regular culture surveys']
  },
  data_privacy: {
    name: 'Data Privacy & Protection',
    level: 'Medium',
    score: 6.8,
    trend: 'Improving',
    controls: 25,
    incidents: 1,
    mitigation: ['Privacy by design', 'Regular audits', 'GDPR compliance checks']
  },
  supply_chain: {
    name: 'Supply Chain Ethics',
    level: 'Medium',
    score: 6.4,
    trend: 'Improving',
    controls: 15,
    incidents: 0,
    mitigation: ['Supplier audits', 'Code of conduct', 'ESG assessments']
  },
  financial: {
    name: 'Financial Misconduct',
    level: 'Low',
    score: 2.9,
    trend: 'Stable',
    controls: 22,
    incidents: 0,
    mitigation: ['Internal controls', 'Segregation of duties', 'Regular reconciliations']
  },
  environmental: {
    name: 'Environmental Impact',
    level: 'Medium',
    score: 5.7,
    trend: 'Improving',
    controls: 14,
    incidents: 1,
    mitigation: ['Sustainability programs', 'Carbon monitoring', 'Waste reduction']
  }
};

// Ethics Training Programs
const TRAINING_PROGRAMS = [
  {
    name: 'Code of Conduct Foundation',
    category: 'Mandatory',
    completionRate: 97.8,
    targetAudience: 'All Employees',
    frequency: 'Annual',
    duration: '45 minutes',
    lastUpdated: '2024-01-15',
    status: 'Active'
  },
  {
    name: 'Anti-Corruption & Bribery',
    category: 'Role-Based',
    completionRate: 94.2,
    targetAudience: 'Sales, Procurement, Leadership',
    frequency: 'Annual',
    duration: '60 minutes',
    lastUpdated: '2024-02-10',
    status: 'Active'
  },
  {
    name: 'Data Privacy Essentials',
    category: 'Mandatory',
    completionRate: 89.6,
    targetAudience: 'All Employees',
    frequency: 'Bi-annual',
    duration: '30 minutes',
    lastUpdated: '2024-03-05',
    status: 'Active'
  },
  {
    name: 'Unconscious Bias Awareness',
    category: 'Recommended',
    completionRate: 76.4,
    targetAudience: 'Managers, HR',
    frequency: 'Annual',
    duration: '90 minutes',
    lastUpdated: '2024-01-20',
    status: 'Active'
  },
  {
    name: 'Supply Chain Ethics',
    category: 'Role-Based',
    completionRate: 91.7,
    targetAudience: 'Procurement, Operations',
    frequency: 'Annual',
    duration: '75 minutes',
    lastUpdated: '2024-02-28',
    status: 'Active'
  },
  {
    name: 'Financial Integrity',
    category: 'Role-Based',
    completionRate: 96.3,
    targetAudience: 'Finance, Accounting, Executives',
    frequency: 'Annual',
    duration: '120 minutes',
    lastUpdated: '2024-01-10',
    status: 'Active'
  }
];

// Policy Management
const POLICIES = [
  {
    name: 'Code of Business Conduct and Ethics',
    version: '3.2',
    category: 'Core Policy',
    lastReview: '2024-01-15',
    nextReview: '2025-01-15',
    owner: 'Chief Ethics Officer',
    acknowledgmentRate: 98.7,
    status: 'Current'
  },
  {
    name: 'Anti-Corruption and Bribery Policy',
    version: '2.1',
    category: 'Compliance',
    lastReview: '2024-02-10',
    nextReview: '2025-02-10',
    owner: 'Legal & Compliance',
    acknowledgmentRate: 96.4,
    status: 'Current'
  },
  {
    name: 'Data Protection and Privacy Policy',
    version: '4.0',
    category: 'Privacy',
    lastReview: '2024-03-01',
    nextReview: '2024-12-01',
    owner: 'Data Protection Officer',
    acknowledgmentRate: 94.8,
    status: 'Current'
  },
  {
    name: 'Supplier Code of Conduct',
    version: '2.3',
    category: 'Supply Chain',
    lastReview: '2024-02-20',
    nextReview: '2025-02-20',
    owner: 'Procurement & Ethics',
    acknowledgmentRate: 89.2,
    status: 'Current'
  },
  {
    name: 'Whistleblower Protection Policy',
    version: '1.4',
    category: 'Governance',
    lastReview: '2024-01-30',
    nextReview: '2025-01-30',
    owner: 'Internal Audit',
    acknowledgmentRate: 92.6,
    status: 'Current'
  },
  {
    name: 'Conflict of Interest Policy',
    version: '2.0',
    category: 'Governance',
    lastReview: '2024-02-15',
    nextReview: '2025-02-15',
    owner: 'HR & Legal',
    acknowledgmentRate: 95.7,
    status: 'Current'
  }
];

// Incident Management
const INCIDENT_CATEGORIES = {
  harassment: { name: 'Harassment & Discrimination', count: 3, resolved: 2, avgResolutionTime: 14.5 },
  corruption: { name: 'Corruption & Bribery', count: 0, resolved: 0, avgResolutionTime: 0 },
  data_breach: { name: 'Data Privacy Breach', count: 1, resolved: 1, avgResolutionTime: 5.2 },
  conflict_interest: { name: 'Conflict of Interest', count: 2, resolved: 2, avgResolutionTime: 8.7 },
  financial: { name: 'Financial Misconduct', count: 0, resolved: 0, avgResolutionTime: 0 },
  supply_chain: { name: 'Supply Chain Violations', count: 1, resolved: 0, avgResolutionTime: 18.3 },
  other: { name: 'Other Ethics Concerns', count: 4, resolved: 3, avgResolutionTime: 12.1 }
};

// Generate comprehensive ethics dashboard data
function generateEthicsDashboardData() {
  const currentDate = new Date();
  const thirtyDaysAgo = new Date(currentDate.getTime() - (30 * 24 * 60 * 60 * 1000));
  const ninetyDaysAgo = new Date(currentDate.getTime() - (90 * 24 * 60 * 60 * 1000));

  return {
    overview: {
      overallEthicsScore: 91.4,
      trend: 'Improving',
      lastAssessment: '2024-09-15',
      totalEmployees: 2847,
      trainedEmployees: 2692,
      trainingCompletionRate: 94.6,
      activeIncidents: 2,
      resolvedIncidents: 8,
      incidentResolutionRate: 80.0,
      policyAcknowledgmentRate: 95.2,
      supplierComplianceRate: 87.8,
      auditFindings: {
        high: 0,
        medium: 3,
        low: 8,
        total: 11
      },
      certifications: {
        iso37001: { status: 'Certified', expiry: '2025-06-30' },
        soc2: { status: 'Certified', expiry: '2025-03-15' },
        gdpr: { status: 'Compliant', lastAssessment: '2024-08-20' }
      }
    },

    frameworkCompliance: ETHICS_FRAMEWORKS,
    
    riskAssessment: {
      overallRiskScore: 5.4, // out of 10, lower is better
      riskLevel: 'Medium',
      categories: ETHICS_RISK_CATEGORIES,
      lastAssessment: '2024-09-01',
      nextAssessment: '2024-12-01',
      mitigation: {
        implemented: 23,
        planned: 8,
        inProgress: 5
      }
    },

    trainingPrograms: {
      programs: TRAINING_PROGRAMS,
      overallCompletionRate: 91.7,
      upcomingDeadlines: [
        {
          program: 'Code of Conduct Foundation',
          dueDate: '2024-12-31',
          pendingUsers: 155,
          risk: 'Medium'
        },
        {
          program: 'Anti-Corruption & Bribery',
          dueDate: '2024-11-15',
          pendingUsers: 89,
          risk: 'High'
        },
        {
          program: 'Data Privacy Essentials',
          dueDate: '2024-10-30',
          pendingUsers: 298,
          risk: 'Medium'
        }
      ]
    },

    policyManagement: {
      policies: POLICIES,
      overallAcknowledgmentRate: 94.5,
      upcomingReviews: [
        {
          policy: 'Data Protection and Privacy Policy',
          dueDate: '2024-12-01',
          owner: 'Data Protection Officer',
          priority: 'High'
        },
        {
          policy: 'Code of Business Conduct and Ethics',
          dueDate: '2025-01-15',
          owner: 'Chief Ethics Officer',
          priority: 'Medium'
        }
      ]
    },

    incidentManagement: {
      categories: INCIDENT_CATEGORIES,
      totalIncidents: 11,
      resolvedIncidents: 8,
      pendingIncidents: 3,
      avgResolutionTime: 11.8, // days
      recentIncidents: [
        {
          id: 'INC-2024-087',
          type: 'Harassment & Discrimination',
          status: 'Under Investigation',
          reportedDate: '2024-09-10',
          severity: 'High',
          assignedTo: 'Sarah Johnson (HR)',
          daysOpen: 16
        },
        {
          id: 'INC-2024-088',
          type: 'Supply Chain Violations',
          status: 'Under Review',
          reportedDate: '2024-09-18',
          severity: 'Medium',
          assignedTo: 'Michael Chen (Procurement)',
          daysOpen: 8
        },
        {
          id: 'INC-2024-089',
          type: 'Other Ethics Concerns',
          status: 'Resolved',
          reportedDate: '2024-09-05',
          severity: 'Low',
          assignedTo: 'Ethics Committee',
          daysOpen: 0,
          resolutionDate: '2024-09-15'
        }
      ]
    },

    auditFindings: {
      recent: [
        {
          id: 'AUD-2024-Q3-12',
          area: 'Third-Party Risk Management',
          severity: 'Medium',
          finding: 'Insufficient due diligence documentation for high-risk vendors',
          status: 'In Progress',
          dueDate: '2024-10-31',
          owner: 'Procurement Team'
        },
        {
          id: 'AUD-2024-Q3-13',
          area: 'Training Compliance',
          severity: 'Low',
          finding: 'Delayed completion of mandatory ethics training by 5% of workforce',
          status: 'Resolved',
          dueDate: '2024-09-30',
          owner: 'HR Learning & Development'
        },
        {
          id: 'AUD-2024-Q3-14',
          area: 'Policy Documentation',
          severity: 'Low',
          finding: 'Minor formatting inconsistencies in policy templates',
          status: 'Resolved',
          dueDate: '2024-09-15',
          owner: 'Legal & Compliance'
        }
      ]
    },

    kpis: {
      ethicsHotlineCalls: {
        current: 23,
        previousPeriod: 18,
        trend: 'Increasing',
        target: 25,
        description: 'Ethics hotline calls (monthly)'
      },
      trainingCompletion: {
        current: 94.6,
        previousPeriod: 91.2,
        trend: 'Improving',
        target: 95.0,
        description: 'Training completion rate (%)'
      },
      policyAcknowledgment: {
        current: 95.2,
        previousPeriod: 93.8,
        trend: 'Improving',
        target: 98.0,
        description: 'Policy acknowledgment rate (%)'
      },
      incidentResolution: {
        current: 11.8,
        previousPeriod: 14.2,
        trend: 'Improving',
        target: 10.0,
        description: 'Average incident resolution time (days)'
      },
      supplierCompliance: {
        current: 87.8,
        previousPeriod: 85.4,
        trend: 'Improving',
        target: 90.0,
        description: 'Supplier ethics compliance rate (%)'
      },
      auditFindings: {
        current: 11,
        previousPeriod: 15,
        trend: 'Improving',
        target: 8,
        description: 'Open audit findings (count)'
      }
    },

    alerts: [
      {
        id: 'ALT-2024-034',
        type: 'Training Deadline',
        severity: 'High',
        message: '89 employees have anti-corruption training due within 30 days',
        action: 'Send reminder notifications and escalate to managers',
        assignedTo: 'HR Learning Team',
        dueDate: '2024-11-15',
        created: '2024-09-20'
      },
      {
        id: 'ALT-2024-035',
        type: 'Incident Follow-up',
        severity: 'Medium',
        message: 'Supply chain violation incident requires stakeholder update',
        action: 'Prepare executive briefing and remediation plan',
        assignedTo: 'Ethics Committee',
        dueDate: '2024-10-01',
        created: '2024-09-18'
      },
      {
        id: 'ALT-2024-036',
        type: 'Audit Finding',
        severity: 'Medium',
        message: 'Third-party risk management findings require attention',
        action: 'Complete vendor due diligence documentation review',
        assignedTo: 'Procurement Team',
        dueDate: '2024-10-31',
        created: '2024-09-15'
      }
    ],

    benchmarking: {
      industryAverages: {
        ethicsScore: 87.2,
        trainingCompletionRate: 89.4,
        incidentResolutionTime: 15.6,
        policyAcknowledgmentRate: 91.8,
        auditFindingsPerYear: 18
      },
      competitorComparison: {
        ethicsScore: { us: 91.4, industry: 87.2, bestInClass: 95.7 },
        trainingCompletion: { us: 94.6, industry: 89.4, bestInClass: 98.2 },
        incidentResolution: { us: 11.8, industry: 15.6, bestInClass: 8.4 },
        supplierCompliance: { us: 87.8, industry: 82.1, bestInClass: 94.3 }
      },
      ranking: {
        overall: 'Top Quartile',
        category: 'Above Average',
        improvement: 'Steady Progress'
      }
    }
  };
}

// Subscriber management
let state = null;
let subscribers = [];

function notifySubscribers() {
  subscribers.forEach(callback => {
    try {
      callback(state);
    } catch (error) {
      console.error('Error notifying subscriber:', error);
    }
  });
}

function getState() {
  if (!state) {
    try {
      const raw = localStorage.getItem(ETHICS_DASHBOARD_KEY);
      return raw ? JSON.parse(raw) : generateEthicsDashboardData();
    } catch (error) {
      console.error('Error loading ethics dashboard data:', error);
      return generateEthicsDashboardData();
    }
  }
  return state;
}

function setState(newState) {
  state = newState;
  try {
    localStorage.setItem(ETHICS_DASHBOARD_KEY, JSON.stringify(state));
    notifySubscribers();
  } catch (error) {
    console.error('Error saving ethics dashboard data:', error);
  }
}

// Public API
export function subscribe(callback) {
  if (typeof callback !== 'function') {
    throw new Error('Callback must be a function');
  }
  
  subscribers.push(callback);
  
  // Immediate callback with current state
  const currentState = getState();
  try {
    callback(currentState);
  } catch (error) {
    console.error('Error in initial callback:', error);
  }
  
  // Return unsubscribe function
  return () => {
    subscribers = subscribers.filter(sub => sub !== callback);
  };
}

// Data accessor functions
export function getDashboardOverview() {
  return getState().overview;
}

export function getFrameworkCompliance() {
  return getState().frameworkCompliance;
}

export function getRiskAssessment() {
  return getState().riskAssessment;
}

export function getTrainingPrograms() {
  return getState().trainingPrograms;
}

export function getPolicyManagement() {
  return getState().policyManagement;
}

export function getIncidentManagement() {
  return getState().incidentManagement;
}

export function getAuditFindings() {
  return getState().auditFindings;
}

export function getKPIs() {
  return getState().kpis;
}

export function getAlerts() {
  return getState().alerts;
}

export function getBenchmarking() {
  return getState().benchmarking;
}

export function getCriticalAlerts() {
  return getState().alerts.filter(alert => alert.severity === 'High');
}

export function getUpcomingDeadlines() {
  const training = getState().trainingPrograms.upcomingDeadlines;
  const policies = getState().policyManagement.upcomingReviews;
  const alerts = getState().alerts.filter(alert => alert.type.includes('Deadline'));
  
  return {
    training,
    policies,
    alerts,
    total: training.length + policies.length + alerts.length
  };
}

export function getPerformanceTrends() {
  const kpis = getKPIs();
  
  return {
    ethicsScore: {
      current: 91.4,
      previous: 89.7,
      trend: 'Improving',
      target: 93.0
    },
    trainingCompletion: {
      current: kpis.trainingCompletion.current,
      previous: kpis.trainingCompletion.previousPeriod,
      trend: kpis.trainingCompletion.trend,
      target: kpis.trainingCompletion.target
    },
    incidentResolution: {
      current: kpis.incidentResolution.current,
      previous: kpis.incidentResolution.previousPeriod,
      trend: kpis.incidentResolution.trend,
      target: kpis.incidentResolution.target
    },
    policyCompliance: {
      current: kpis.policyAcknowledgment.current,
      previous: kpis.policyAcknowledgment.previousPeriod,
      trend: kpis.policyAcknowledgment.trend,
      target: kpis.policyAcknowledgment.target
    }
  };
}

// Action functions
export function refreshData() {
  const newState = generateEthicsDashboardData();
  setState(newState);
  
  addAuditLog({
    user: 'system',
    action: 'data_refresh',
    target: 'ethics_dashboard',
    status: 'success',
    details: 'Ethics dashboard data refreshed'
  });
}

export function updateTrainingCompletion(programId, completionRate) {
  const currentState = getState();
  const updatedPrograms = currentState.trainingPrograms.programs.map(program => {
    if (program.name === programId) {
      return { ...program, completionRate };
    }
    return program;
  });
  
  const updatedState = {
    ...currentState,
    trainingPrograms: {
      ...currentState.trainingPrograms,
      programs: updatedPrograms
    }
  };
  
  setState(updatedState);
  
  addAuditLog({
    user: 'system',
    action: 'training_update',
    target: programId,
    status: 'success',
    details: `Training completion rate updated to ${completionRate}%`
  });
}

export function resolveIncident(incidentId) {
  const currentState = getState();
  const updatedIncidents = currentState.incidentManagement.recentIncidents.map(incident => {
    if (incident.id === incidentId) {
      return { 
        ...incident, 
        status: 'Resolved', 
        resolutionDate: new Date().toISOString().split('T')[0],
        daysOpen: 0 
      };
    }
    return incident;
  });
  
  const updatedState = {
    ...currentState,
    incidentManagement: {
      ...currentState.incidentManagement,
      recentIncidents: updatedIncidents,
      resolvedIncidents: currentState.incidentManagement.resolvedIncidents + 1,
      pendingIncidents: Math.max(0, currentState.incidentManagement.pendingIncidents - 1)
    }
  };
  
  setState(updatedState);
  
  addAuditLog({
    user: 'system',
    action: 'incident_resolved',
    target: incidentId,
    status: 'success',
    details: `Ethics incident ${incidentId} resolved`
  });
}

// Initialize service
if (typeof window !== 'undefined') {
  state = generateEthicsDashboardData();
  
  // Save to localStorage on page unload
  window.addEventListener('beforeunload', () => {
    if (state) {
      try {
        localStorage.setItem(ETHICS_DASHBOARD_KEY, JSON.stringify(state));
      } catch (error) {
        console.error('Error saving ethics dashboard data on unload:', error);
      }
    }
  });
}

// Restore state from localStorage on initialization
if (typeof window !== 'undefined') {
  try {
    const saved = localStorage.getItem(ETHICS_DASHBOARD_KEY);
    if (saved) {
      const data = JSON.parse(saved);
      if (data && typeof data === 'object') {
        state = data;
      } else {
        state = generateEthicsDashboardData();
      }
    } else {
      state = generateEthicsDashboardData();
    }
  } catch (error) {
    console.error('Error loading saved ethics dashboard data:', error);
    state = generateEthicsDashboardData();
  }
}

// Export current state for debugging
export function getCurrentState() {
  return {
    ethicsDashboard: state,
    subscribers: subscribers.length,
    frameworks: Object.keys(ETHICS_FRAMEWORKS),
    riskCategories: Object.keys(ETHICS_RISK_CATEGORIES),
    trainingPrograms: TRAINING_PROGRAMS.length,
    policies: POLICIES.length
  };
}